import { Component, Input, OnChanges } from '@angular/core';
import { ListItem, TabsData, UtilService } from '@material-library';
import { BlendEvent, FileItem, HistoryItem, LinkItem } from 'src/app/shared/models';
import { ShareService } from 'src/app/shared/services/share.service';
import { environment } from '../../../environments/environment';
import { DatePipe } from '@angular/common';
import { LoggerService } from 'src/app/core/logger.service';
import { LinkListService } from 'src/app/links/services/link-list.service';
import { ApiService } from 'src/app/core/api.service';
import { B64Pipe } from 'src/app/shared/pipes/b64.pipe';
import { FileItemService } from 'src/app/file/file-item.service';
import { FileListComponent } from '../file-list/file-list.component';
import { Router } from '@angular/router';
import { BlendService } from 'src/app/shared/services/blend.service';

@Component({
  selector: 'app-file-info',
  templateUrl: './file-info.component.html',
  styleUrls: ['./file-info.component.scss']
})
export class FileInfoComponent implements OnChanges {
  @Input()
  public selectedRows: FileItem[] = [];
  @Input()
  public histId: number;
  @Input()
  public activeTab: string = 'Info';

   /*
    flag to determine if file into is getting shown for deleted file
  */
    @Input()
    public isDeleted: boolean = false;

    @Input() public fileListComponent!:FileListComponent;


  public infoTabs: TabsData[] = [];
  public rowData: FileItem;
  public infoListItems: ListItem[] = [];
  public sharedFolderItems: ListItem[] = [];
  public sharedFileItems: ListItem[] = [];
  public linkInfo: ListItem[] = [];
  public historyItems: ListItem[] = [];
  public team_shared_members = [];
  public avatarEndpoint = `${environment.logohost}avatar/saved/`;
  public shareLinkUrl: string;
  public historyResponse: any;
  public isViewFullHistory: boolean;
  public isLoading: boolean = false;

  constructor(
    private shareService: ShareService,
    private linkList: LinkListService,
    public logger: LoggerService,
    public datePipe: DatePipe,
    private apiService: ApiService,
    public b64: B64Pipe,
    public utilService: UtilService,
    protected fileItemService: FileItemService,
    private router: Router,
    private blendService: BlendService,
  ) {
  }

  async ngOnChanges(): Promise<void> {
    if (this.selectedRows.length === 1) {
      this.rowData = this.selectedRows[0];
      this.historyItems = [];
      this.infoListItems = [];
      this.loadTabs();
      await this.loadInfoPanel();
    }
  }

  loadTabs () {
    this.infoTabs = [
      { label: 'Info', activeLink: this.activeTab === 'Info' },
      { label: 'Sharing', activeLink: this.activeTab === 'Sharing', disabled: this.isDeleted }
    ];
    if (this.rowData.is_dir === 0) {
      this.infoTabs = [
        ...this.infoTabs,
        { label: 'History', activeLink: this.activeTab === 'History', disabled: this.isDeleted }
      ];
    }
  }

  onTabChange = (event: TabsData) => {
    this.activeTab = event.label;
    this.loadTabs();
    this.loadInfoPanel();

    switch (event.label) {
      case 'Info':
        this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: 'View item info (Info panel)' });
        break;
      case 'Sharing':
        this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: 'View sharing (Info panel)' });
        break;
      case 'History':
        this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: 'View history (Info panel)' });
        break;
    }

  };

  async loadInfoPanel() {
    if (this.activeTab === 'History') {
      if (this.historyItems.length > 0) {
        return;
      }
      this.isLoading = true;
      try {
        await this.loadHistory();
      } catch (error) {
        this.logger.error(error);
      } finally {
        this.isLoading = false;
      }
      return;
    } else {
      if (this.infoListItems.length > 0) {
        return;
      }
      this.isLoading = true;
      this.infoListItems = [];
      try {
        let ownerName = '';
        let total_collaborators = '';
        this.team_shared_members = [];
        if (this.rowData.is_shared === 1 && this.rowData.is_dir === 1) {
          const shareObj = await this.shareService.getShare(this.rowData.share_id);
          const owner = shareObj.people.find(people => people.permissions.perOWNER);
          ownerName = owner?.displayname;      
          this.team_shared_members = [];
          shareObj?.people?.forEach(people => {
            const url = `${this.avatarEndpoint}${people.userId}`;
            const intials = people.displayname;
            this.team_shared_members.push({ 
              url: url,
              initials: intials,
              tooltip: people.displayname
            });
          });
          total_collaborators = `${this.team_shared_members.length} + collaborators`;

          if(owner) {
            this.infoListItems = [
              {
                primaryText: ownerName,
                secondaryText: 'OWNER',
                leadAdornmentConfig: {
                  type: 'avatar',
                  enabled: true,
                  size: 'large',
                  avatarConfig: {
                    type: 'avatar',
                    size: 48,
                    url: `${this.avatarEndpoint}${owner.userId}`,
                    initials: ownerName
                  }
                }
              }
            ]
          }     
        }

        this.infoListItems = [
          ...this.infoListItems,
          ...(this.rowData.is_dir === 0 ? [{
            primaryText: this.rowData.kind.toUpperCase(),
            overlineText: 'FILE TYPE'
          }, {
            primaryText: this.rowData.filesize,
            overlineText: 'FILE SIZE'
          }] : []),
          ...(this.rowData.is_dir === 0 ? [{
            primaryText: this.shareService.formatTimestamp(this.rowData.usertime),
            overlineText: 'LAST MODIFIED'
          }] : []),
          {
            primaryText: this.rowData.id,
            overlineText: 'ID'
          }
        ];

        const createShareListItem: ListItem = {
          primaryText: 'Share with my team',
          secondaryText: 'Collaborate and share this folder seamlessly within your team',
          trail1AdornmentConfig: {
            enabled: true,
            type: 'button',
            size: 'large',
            buttonConfig: {
              label: 'Share',
              size: 'large',
              color: 'default',
              type: 'outlined'
            },
            onClick:()=>{
              if(this.fileListComponent){
                this.fileListComponent.onInviteCollaborators(this.rowData)
              }
            }
          }
        };

        const manageShareItem: ListItem = {
          primaryText: 'Manage my shared folder',
          secondaryText: total_collaborators,
          trail1AdornmentConfig: {
            enabled: true,
            type: 'button',
            size: 'large',
            buttonConfig: {
              label: 'Manage',
              size: 'large',
              color: 'default',
              type: 'outlined'
            },
            onClick:()=>{
              if(this.fileListComponent){
                this.fileListComponent.onManageCollaborators(this.rowData)
              }
              this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: 'Manage share (Info panel)' });
            }
          }
        }

        this.sharedFolderItems = [
          ...(this.rowData.is_shared === 0 ? [{
            ...createShareListItem
          }] : [{
            ...manageShareItem
          }])
        ];

        const createLinkItem: ListItem = {
          primaryText: 'Share as a link',
          secondaryText: 'Create and share a link',
          trail1AdornmentConfig: {
            enabled: true,
            type: 'button',
            size: 'large',
            buttonConfig: {
              label: 'Create',
              size: 'large',
              color: 'default',
              type: 'outlined'
            },
            onClick:()=>{
              if(this.fileListComponent){
                this.fileListComponent.onLinkDialog(this.rowData)
              }
            }
          }
        };

        const manageLinkItem: ListItem = {
          primaryText: 'Manage my link settings',
          secondaryText: 'View and change your link settings',
          trail1AdornmentConfig: {
            enabled: true,
            type: 'button',
            size: 'large',
            buttonConfig: {
              label: 'Manage',
              size: 'large',
              color: 'default',
              type: 'outlined'
            },
            onClick:()=>{
              if(this.fileListComponent){
                this.fileListComponent.onLinkDialog(this.rowData)
              }
            }
          }
        }

        this.sharedFileItems = [
          ...(this.rowData.is_publink === 0 ? [{
            ...createLinkItem
          }] : [{
            ...manageLinkItem
          }])
        ];

        const data = await this.linkList.getLink(this.rowData.sync_id, this.rowData.name);
        this.shareLinkUrl = (data as LinkItem).linkUrl;
        this.linkInfo = [{
          primaryText: data.download_count.toString(),
          overlineText: 'LINK DOWNLOADS'
        }];
      } catch (error) {
        this.logger.error(error);
      } finally {
        this.isLoading = false;
      }
    }
  }

  public async loadHistory(): Promise<void> {
    // Call the historyApi first
    this.historyResponse = await this.apiService.execute('pathhistory', {
      sync_id: this.rowData.sync_id,
      hist_id: this.histId
    });
    // Extract necessary data from the response for further use
    this.historyItems = this.decorateHistoryItems(this.historyResponse.events || [], false);
  }

  public decorateHistoryItems(events: HistoryItem[], showFullHistory: boolean = false): ListItem[] {
    this.isViewFullHistory = showFullHistory;
    let i,
      len,
      action,
      curEvent: HistoryItem,
      lastEvent;
    const retEvents = [],
      prevEvents: { [key: number]: HistoryItem } = {};

    // iterating events in reverse order and storing in retEvents
    for (i = events.length - 1, len = events.length; i >= 0; i--) {
      action = undefined;
      curEvent = events[i];
      lastEvent = prevEvents[curEvent.sync_id];

      if (!curEvent) {
        continue;
      }

      // don't duplicate rows in the path history
      if (
        lastEvent &&
        lastEvent.blob_id == curEvent.blob_id &&
        lastEvent.event_type == curEvent.event_type &&
        lastEvent.size == curEvent.size
      ) {
        continue;
      }
      switch (parseInt(curEvent.event_type, 10)) {
        case -2:
          action = 'Purged';
          break;
        case -1:
          action = 'Deleted';
          break;
        case 0:
          action = 'Pending';
          break;
        case 1:
          action = 'Added';
          break;
        case 2:
          action = 'Restored';
          break;
        case 3:
          action = 'Edited';
          break;
        default:
          action = '';
      }
      curEvent.action_text = action;
      prevEvents[curEvent.sync_id] = curEvent;
      retEvents.push({
        primaryText: this.rowData?.name || '',
        secondaryText: `<span class="body-2">${curEvent.action_text} by ${this.b64.transform(curEvent.event_username)}</span>`,
        tertiaryText: `${this.utilService.formatDate(new Date(curEvent.date))} | ${this.utilService.bytesToSize(curEvent.size)}`,
        overlineText: '',
        ...(i === (len - 1) ? {trail2AdornmentConfig: {
          enabled: true,
          type: 'chip',
          size: 'medium',
          chipConfig: {
            size: 'medium',
            label:  'Current Version',
          },
        }}:{}),
        leadAdornmentConfig: {
          type: 'avatar',
          enabled: true,
          size: 'large',
          avatarConfig: { type: 'avatar', size: 32, url: `${this.avatarEndpoint}${curEvent.event_user_id}` , initials: this.b64.transform(curEvent.event_username) },
        },
        divider:true
      });
    }

    // Return only the last 5 events if conditions are met
    if (events.length > 0 && !showFullHistory) {
      return retEvents.slice(0, 5);
    }

    // Return all events in reverse order
    return [...retEvents];
  }

  public onFullHistory = (event: Event) => {
    this.router.navigate([`files/file-history/${this.rowData.sync_id}`])
    
    // this.historyItems = this.decorateHistoryItems(this.historyResponse.events || [], true);
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

}
